import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { X } from 'lucide-react';

interface WaterStageProps {
  text: string;
  typingDurationMs: number;
  isVisible: boolean;
  onDismiss?: () => void;
}

const WAVE_SVG_1 = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 60' preserveAspectRatio='none'%3E%3Cpath d='M0,35 C100,55 250,10 400,35 C550,60 700,5 900,35 C1050,55 1150,15 1200,35 L1200,60 L0,60 Z' fill='rgba(57%2C148%2C255%2C0.6)'/%3E%3C/svg%3E")`;
const WAVE_SVG_2 = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 70' preserveAspectRatio='none'%3E%3Cpath d='M0,42 C150,65 300,15 500,42 C680,68 850,10 1050,42 C1130,58 1170,22 1200,42 L1200,70 L0,70 Z' fill='rgba(57%2C148%2C255%2C0.35)'/%3E%3C/svg%3E")`;
const WAVE_SVG_3 = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 80' preserveAspectRatio='none'%3E%3Cpath d='M0,55 C80,38 200,70 380,50 C520,34 660,72 830,50 C970,32 1100,65 1200,50 L1200,80 L0,80 Z' fill='rgba(57%2C148%2C255%2C0.18)'/%3E%3C/svg%3E")`;

export function WaterStage({ text, typingDurationMs, isVisible, onDismiss }: WaterStageProps) {
  const [displayed, setDisplayed] = useState('');

  useEffect(() => {
    if (!text) { setDisplayed(''); return; }
    setDisplayed('');
    let i = 0;
    // Complete typing within 75% of the audio duration, up to 40ms per char
    const charDelay = Math.min(40, (typingDurationMs * 0.75) / Math.max(text.length, 1));
    const id = setInterval(() => {
      i++;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) clearInterval(id);
    }, charDelay);
    return () => clearInterval(id);
  }, [text, typingDurationMs]);

  const cursorVisible = displayed.length < text.length;

  return (
    <>
      <style>{`
        @keyframes _wf  { from { transform: translateX(0); }    to { transform: translateX(-50%); } }
        @keyframes _wb  { from { transform: translateX(-50%); } to { transform: translateX(0); } }
        @keyframes _ws  { from { transform: translateX(-25%); } to { transform: translateX(-75%); } }
        @keyframes _yb1 { 0%,100% { transform: translateY(0px);  } 50% { transform: translateY(-7px); } }
        @keyframes _yb2 { 0%,100% { transform: translateY(-3px); } 50% { transform: translateY(6px);  } }
        @keyframes _yb3 { 0%,100% { transform: translateY(4px);  } 50% { transform: translateY(-4px); } }
        @keyframes _cb  { 0%,100% { opacity:1; } 50% { opacity:0; } }
      `}</style>

      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: isVisible ? '0%' : '100%' }}
        transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: '15vh',
          minHeight: 80,
          zIndex: 9990,
          overflow: 'hidden',
          willChange: 'transform',
          WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 18%)',
          maskImage: 'linear-gradient(to bottom, transparent 0%, black 18%)',
        }}
      >
        {/* Water fill */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to bottom, rgba(57,148,255,0.22) 0%, rgba(57,148,255,0.50) 35%, rgba(30,100,200,0.84) 100%)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
        }} />

        {/* Wave 1 — foreground, fastest + gentle bob */}
        <div style={{ position: 'absolute', top: -28, left: 0, right: 0, height: 58, animation: '_yb1 4.2s ease-in-out infinite' }}>
          <div style={{
            position: 'absolute', top: 0, left: 0,
            width: '200%', height: '100%',
            animation: '_wf 7s linear infinite',
            backgroundImage: WAVE_SVG_1,
            backgroundSize: '50% 100%',
            backgroundRepeat: 'repeat-x',
          }} />
        </div>

        {/* Wave 2 — mid, reversed + offset bob */}
        <div style={{ position: 'absolute', top: -42, left: 0, right: 0, height: 66, animation: '_yb2 5.8s ease-in-out infinite', animationDelay: '-1.9s' }}>
          <div style={{
            position: 'absolute', top: 0, left: 0,
            width: '200%', height: '100%',
            animation: '_wb 11s linear infinite',
            backgroundImage: WAVE_SVG_2,
            backgroundSize: '50% 100%',
            backgroundRepeat: 'repeat-x',
          }} />
        </div>

        {/* Wave 3 — deep background, slowest + phase-shifted bob */}
        <div style={{ position: 'absolute', top: -54, left: 0, right: 0, height: 80, animation: '_yb3 7.5s ease-in-out infinite', animationDelay: '-3.7s' }}>
          <div style={{
            position: 'absolute', top: 0, left: 0,
            width: '200%', height: '100%',
            animation: '_ws 14s linear infinite',
            backgroundImage: WAVE_SVG_3,
            backgroundSize: '50% 100%',
            backgroundRepeat: 'repeat-x',
          }} />
        </div>

        {/* Text */}
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingTop: 22,
          paddingLeft: 80,
          paddingRight: 80,
        }}>
          <p style={{
            fontFamily: "'Google Sans Flex', Inter, system-ui, sans-serif",
            fontWeight: 700,
            fontSize: 'clamp(13px, 1.45vw, 17px)',
            color: 'white',
            textShadow: '0 1px 8px rgba(0,0,0,0.4)',
            letterSpacing: '-0.01em',
            textAlign: 'center',
            maxWidth: '80ch',
            lineHeight: 1.45,
            margin: 0,
          }}>
            {displayed}
            <span style={{
              display: 'inline-block',
              width: 2,
              height: '0.95em',
              background: 'rgba(255,255,255,0.9)',
              marginLeft: 3,
              verticalAlign: 'text-bottom',
              borderRadius: 1,
              animation: cursorVisible ? '_cb 0.9s step-end infinite' : 'none',
              opacity: cursorVisible ? 1 : 0,
            }} />
          </p>
        </div>

        {/* Skip button */}
        {onDismiss && (
          <button
            onClick={onDismiss}
            style={{
              position: 'absolute',
              right: 18,
              bottom: 14,
              color: 'rgba(255,255,255,0.8)',
              background: 'rgba(255,255,255,0.14)',
              border: '1px solid rgba(255,255,255,0.22)',
              borderRadius: 8,
              padding: '4px 11px 4px 8px',
              cursor: 'pointer',
              fontSize: 12,
              fontFamily: "'Google Sans Flex', Inter, sans-serif",
              fontWeight: 500,
              backdropFilter: 'blur(6px)',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              lineHeight: 1.6,
            }}
          >
            <X size={11} />
            Skip tour
          </button>
        )}
      </motion.div>
    </>
  );
}
